import { useEffect, useState, useCallback } from 'react'
import { UnpaidDocumentsFragment } from 'src/api/api.generated'
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-pro'
import { HicDataGridPro } from 'src/app/components/hic-vapor/datagrid/datagrid'
import { UnpaidDocumentsPaidInFullQuotasType } from 'src/app/components/payment/payment-distribution/payment-distribution.types'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import { useTranslation } from 'react-i18next'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import { keysOf } from 'src/utils/utils'

export type UnpaidDocumentSelectionRow = {
  id: string
  numberExtended: string
  documentDate: Date
  totalAmount: number
  unpaidAmount: number
  expireDate?: Date | null
}

type UnpaidDocumentsSelectionGridProps = {
  unpaidDocuments: UnpaidDocumentsFragment[]
  paidInFullDocumentsQuotas?: UnpaidDocumentsPaidInFullQuotasType
  onChange?: (ids: string[]) => void
  comeFromUnpaidDocumentId?: string
}

export const UnpaidDocumentsSelectionGrid = (props: UnpaidDocumentsSelectionGridProps) => {
  const { unpaidDocuments, onChange, comeFromUnpaidDocumentId } = props

  const [rows, setRows] = useState<UnpaidDocumentSelectionRow[]>([])
  const [selectionModel, setSelectionModel] = useState<string[]>([])

  useEffect(() => {
    setRows(
      unpaidDocuments.map<UnpaidDocumentSelectionRow>((d) => ({
        documentDate: d.documentDate,
        expireDate: d.documentPaymentDetails.filter((x) => x.expireDate)?.[0].expireDate,
        id: d.id,
        numberExtended: d.numberExtended,
        totalAmount: d.totalAmount,
        unpaidAmount: d.unpaidAmount,
        residualAmount: d.residualAmount,
      })),
    )
  }, [unpaidDocuments])

  const columns = useColumns()

  const handleOnSelectionModelChange = useCallback(
    (newSelectionModel: GridRowSelectionModel) => {
      const selections = newSelectionModel.map((i) => i.toString())
      setSelectionModel(selections)
      onChange?.(selections)
    },
    [setSelectionModel, onChange],
  )

  const putDocumentIdOnTop = useCallback(
    (documentId: string) => {
      const updRows = [...rows]
      updRows.sort((a, b) => {
        if (a.id === documentId) return -1
        if (b.id === documentId) return 1
        return 0
      })
      return updRows
    },
    [rows],
  )

  useEffect(() => {
    if (comeFromUnpaidDocumentId) setRows(putDocumentIdOnTop(comeFromUnpaidDocumentId))
  }, [comeFromUnpaidDocumentId])

  const getRowClassName = useCallback(
    (params): string => {
      if (params.id === comeFromUnpaidDocumentId) return 'tsh-bold-row'
      return ''
    },
    [comeFromUnpaidDocumentId],
  )
  return (
    <>
      <HicDataGridPro //
        columns={columns}
        rows={rows}
        editMode="row"
        autoHeight
        disableRowSelectionOnClick
        checkboxSelection
        rowSelectionModel={selectionModel}
        hideFooter
        componentsProps={{
          baseCheckbox: { size: 'small' },
        }}
        onRowSelectionModelChange={handleOnSelectionModelChange}
        getRowClassName={getRowClassName}
      />
    </>
  )
}

const useColumns = (): GridColDef[] => {
  const { t } = useTranslation()
  const { currency } = useTenant()
  const { formatTableCurrency } = useFormatCurrency()
  const formatDate = useFormatDate()

  const field = keysOf<UnpaidDocumentSelectionRow>()
  return [
    {
      field: field('numberExtended'),
      headerName: t('pms.accounting.documents.progressive'),
      flex: 1,
      sortable: true,
      valueFormatter: ({ value }) => value,
    },
    {
      field: field('documentDate'),
      headerName: t('pms.accounting.documents.document_date'),
      type: 'date',
      flex: 1,
      valueFormatter: ({ value }) => formatDate(value),
      sortable: true,
    },
    {
      field: field('totalAmount'),
      headerName: t('commons.document_total'),
      type: 'number',
      flex: 1,
      valueFormatter: ({ value }) => formatTableCurrency(value ?? 0, currency),
      sortable: false,
    },
    {
      field: field('unpaidAmount'),
      headerName: t('commons.unpaid_amount'),
      type: 'number',
      flex: 1,
      valueFormatter: ({ value }) => formatTableCurrency(value ?? 0, currency),
      sortable: false,
    },
    {
      field: field('expireDate'),
      headerName: t('commons.unpaid_expire_date'),
      type: 'date',
      flex: 1,
      valueFormatter: ({ value }) => formatDate(value),
      sortable: true,
    },
  ]
}
